/**@param date {string}*/
export function toOffsetDateTime(date) {
  if (date === undefined || date === null) {
    throw new TypeError("date string is null or undefined");
  }
  if (!date) throw new Error("date string is empty");

  const localDate = new Date(`${date}:00`);

  if (Number.isNaN(localDate.getTime())) {
    throw new Error(`invalid input date string: ${date}`);
  }

  const utcDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60 * 1000);
  return utcDate.toISOString().replace(".000", "");
}

export function fromOffsetDateTime(date) {
  if (date === undefined || date === null) {
    throw new Error("date string is null or undefined");
  }
  if (!date) throw new Error("empty date string");

  const utcDate = new Date(date);
  if (Number.isNaN(utcDate.getTime())) {
    throw new Error(`invalid input date string: ${date}`);
  }
  const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000);

  return yyyyMMddThhmmFormat(localDate);
}

/**@param date {Date}*/
function yyyyMMddThhmmFormat(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * The site's one time zone. Publish dates are Swedish dates: the server renders in whatever
 * zone the container happens to run in (UTC) and the browser in the visitor's, so without
 * pinning it an article published late in the evening shows one date in the server-rendered
 * HTML and another after hydration — a mismatch, and a wrong date for half the day.
 */
export const SITE_TIME_ZONE = "Europe/Stockholm";

let publishDateFormat;

/**
 * A publish date as `YYYY-MM-DD`, identical on the server and in the browser.
 *
 * `toLocaleDateString()` with no arguments follows the *host's* locale, which is en-US on the
 * server and whatever the visitor picked in the browser — the two never agree. The parts are
 * assembled by hand rather than taking the formatter's own output so that even a difference in
 * ICU's pattern for a locale can't change the result.
 *
 * @param {Date|string|number} value
 * @returns {string} the formatted date, or "" when there is no usable date
 */
export function formatPublishDate(value) {
  if (value === null || value === undefined || value === "") return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  try {
    publishDateFormat ??= new Intl.DateTimeFormat("sv-SE", {
      timeZone: SITE_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const parts = Object.fromEntries(
      publishDateFormat.formatToParts(date).map(({ type, value: part }) => [type, part])
    );
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    // A runtime built without the time zone data — UTC is still the same answer everywhere.
    return date.toISOString().slice(0, 10);
  }
}
